import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';

const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || '';

const formatAmount = (value) => Number(value || 0).toLocaleString('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0
});

const formatDate = (value) => {
  if (!value) return '--';
  return new Date(value).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
};

const titleCase = (value) => {
  if (!value) return '--';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const DetailItem = ({ label, value }) => (
  <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
    <div className="text-[11px] font-semibold uppercase text-slate-500">{label}</div>
    <div className="text-sm font-medium text-slate-900 break-words">{value || '--'}</div>
  </div>
);

const Section = ({ title, icon, children }) => (
  <section className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
    <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-100/80 px-4 py-3">
      <div className="h-7 w-7 rounded-md bg-white text-[#009ef7] flex items-center justify-center shadow-sm">
        <i className={`bi ${icon} text-xs`}></i>
      </div>
      <h3 className="text-sm font-semibold text-slate-900 m-0">{title}</h3>
    </div>
    <div className="p-4">{children}</div>
  </section>
);

const BookingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBooking();
  }, [id]);

  const fetchBooking = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`bookingDetail/${id}`);
      if (response.status) {
        setBooking(response.data);
      } else {
        toast.error(response.message || 'Booking not found');
        navigate('/view-bookings');
      }
    } catch (error) {
      console.error('Booking detail error:', error);
      toast.error('Failed to load booking details');
      navigate('/view-bookings');
    } finally {
      setLoading(false);
    }
  };

  const totals = useMemo(() => {
    const totalAmount = Number(booking?.packageDetail?.totalAmount || 0);
    const paidAmount = Array.isArray(booking?.payments)
      ? booking.payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0)
      : 0;
    return {
      totalAmount,
      paidAmount,
      dueAmount: Math.max(totalAmount - paidAmount, 0)
    };
  }, [booking]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#009ef7' }}></div>
        <span className="ml-2 text-gray-600">Loading...</span>
      </div>
    );
  }

  if (!booking) return null;

  const sharing = booking.packageDetail?.sharing || {};
  const documentUrl = booking.identityDocument ? `${MEDIA_URL}${booking.identityDocument}` : null;

  return (
    <div className="p-0 space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <nav className="flex flex-wrap items-center text-xs sm:text-sm">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700 no-underline">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/view-bookings" className="text-gray-500 hover:text-gray-700 no-underline">Booking Manage</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">{booking.invoiceNumber}</span>
        </nav>
        <button type="button" onClick={() => navigate('/view-bookings')} className="h-9 px-4 text-sm rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200">
          Back
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg bg-slate-900 text-white p-4">
          <div className="text-xs text-slate-300 uppercase">Total Amount</div>
          <div className="text-2xl font-semibold">{formatAmount(totals.totalAmount)}</div>
        </div>
        <div className="rounded-lg bg-emerald-600 text-white p-4">
          <div className="text-xs text-emerald-100 uppercase">Paid Amount</div>
          <div className="text-2xl font-semibold">{formatAmount(totals.paidAmount)}</div>
        </div>
        <div className="rounded-lg bg-rose-600 text-white p-4">
          <div className="text-xs text-rose-100 uppercase">Due Amount</div>
          <div className="text-2xl font-semibold">{formatAmount(totals.dueAmount)}</div>
        </div>
      </div>

      <Section title="Booking Summary" icon="bi-receipt">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <DetailItem label="Invoice" value={booking.invoiceNumber} />
          <DetailItem label="Booking Status" value={titleCase(booking.bookingStatus)} />
          <DetailItem label="Payment Status" value={titleCase(booking.paymentStatus)} />
          <DetailItem label="Created At" value={formatDate(booking.createdAt)} />
        </div>
      </Section>

      <Section title="Traveller Details" icon="bi-person-vcard">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <DetailItem label="Traveller" value={booking.travellerName} />
          <DetailItem label="Email" value={booking.email} />
          <DetailItem label="Phone" value={booking.phone} />
          <DetailItem label="Location" value={booking.location} />
          <DetailItem label="Identity Type" value={booking.identityType} />
          <DetailItem label="Identity Number" value={booking.identityNumber} />
          <DetailItem label="Emergency Name" value={booking.emergencyContact?.name} />
          <DetailItem label="Emergency Phone" value={booking.emergencyContact?.phone} />
        </div>
        {documentUrl && (
          <a href={documentUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-3 text-sm text-[#009ef7] no-underline">
            <i className="bi bi-file-earmark-text"></i>
            View Identity Document
          </a>
        )}
      </Section>

      <Section title="Package Details" icon="bi-suitcase2">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <DetailItem label="Package" value={booking.packageDetail?.name} />
          <DetailItem label="Duration" value={booking.packageDetail?.duration} />
          <DetailItem label="Departure" value={booking.departure} />
          <DetailItem label="Tour Type" value={booking.tourType} />
          <DetailItem label="Adults" value={booking.numberOfTravellers} />
          <DetailItem label="Children" value={booking.children} />
          <DetailItem label="Child Included In Total" value={booking.includeChildrenInTotal ? 'Yes' : 'No'} />
          <DetailItem label="Travel Date" value={`${booking.travelStartDate ? new Date(booking.travelStartDate).toLocaleDateString('en-IN') : '--'} to ${booking.travelEndDate ? new Date(booking.travelEndDate).toLocaleDateString('en-IN') : '--'}`} />
          <DetailItem label="Base Amount / Person" value={formatAmount(booking.packageDetail?.amount)} />
          <DetailItem label="GST (%)" value={booking.packageDetail?.gst} />
          <DetailItem label="Total Amount" value={formatAmount(booking.packageDetail?.totalAmount)} />
        </div>

        {sharing?.enabled && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs sm:text-sm border border-slate-200 rounded-lg overflow-hidden">
              <thead className="bg-slate-100 text-slate-600 uppercase text-xs">
                <tr>
                  <th className="px-3 py-2 text-left">Sharing</th>
                  <th className="px-3 py-2 text-left">Members</th>
                  <th className="px-3 py-2 text-left">Price / Person</th>
                  <th className="px-3 py-2 text-left">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {['quad', 'triple', 'double'].map((type) => (
                  <tr key={type}>
                    <td className="px-3 py-2 font-medium capitalize">{type}</td>
                    <td className="px-3 py-2">{sharing[type]?.members || 0}</td>
                    <td className="px-3 py-2">{formatAmount(sharing[type]?.pricePerPerson)}</td>
                    <td className="px-3 py-2">{formatAmount(sharing[type]?.totalAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Payment History" icon="bi-credit-card">
        {booking.payments?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-slate-100 text-slate-600 uppercase text-xs">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Amount</th>
                  <th className="px-3 py-2 text-left">Mode</th>
                  <th className="px-3 py-2 text-left">Transaction ID</th>
                  <th className="px-3 py-2 text-left">Paid At</th>
                  <th className="px-3 py-2 text-left">Remarks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {booking.payments.map((payment, index) => (
                  <tr key={payment._id || index}>
                    <td className="px-3 py-2">{index + 1}</td>
                    <td className="px-3 py-2 font-semibold">{formatAmount(payment.amount)}</td>
                    <td className="px-3 py-2">{payment.paymentMode || '--'}</td>
                    <td className="px-3 py-2">{payment.transactionId || '--'}</td>
                    <td className="px-3 py-2">{formatDate(payment.paidAt)}</td>
                    <td className="px-3 py-2">{payment.remarks || '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-sm text-slate-500">No payments added yet.</div>
        )}
      </Section>

      {booking.notes && (
        <Section title="Notes" icon="bi-journal-text">
          <p className="text-sm text-slate-700 m-0 whitespace-pre-wrap">{booking.notes}</p>
        </Section>
      )}
    </div>
  );
};

export default BookingDetail;
